import { createAuthClient } from '@neondatabase/auth';

import { AppApiClient, AppApiError, type AccountProfileDto } from './AppApiClient';
import { loadOrCreateGuestIdentity, type GuestIdentity } from './GuestIdentity';
import { NeonSessionTokenProvider } from './NeonSessionTokenProvider';
import type { OnlineSession } from '../online/OnlineIdentity';
import type { ChatSettings } from '../chat/ChatTypes';
import type { MatchmakingSearchRequest, MatchPlaylist } from '../online/MatchmakingContracts';
import type { OnlineSettings, PublicOnlineSettings } from '../online/OnlineSettings';

const ONLINE_GUEST_STORAGE_KEY = 'rocket-soccer.online-guest.v1';

interface AuthenticatedIdentity {
  readonly kind: 'authenticated';
  readonly email: string | null;
  readonly profile: AccountProfileDto;
}

interface GuestSessionIdentity {
  readonly kind: 'guest';
  readonly guest: GuestIdentity;
}

export type BrowserIdentity = GuestSessionIdentity | AuthenticatedIdentity;

export class BrowserIdentityService {
  private readonly guest = loadOrCreateGuestIdentity();
  private readonly authUrl = (import.meta.env.VITE_NEON_AUTH_URL as string | undefined)?.trim();
  private readonly auth = this.authUrl ? createAuthClient(this.authUrl) : null;
  private readonly tokenProvider = this.auth ? new NeonSessionTokenProvider(this.auth) : null;
  private readonly api = new AppApiClient(() => this.accessToken());
  private current: BrowserIdentity = { kind: 'guest', guest: this.guest };
  private identityVersion = 0;
  private profileVersion = 0;
  private onlineSession?: { identity: BrowserIdentity; session: OnlineSession };
  private onlineRequest?: { identity: BrowserIdentity; version: number; promise: Promise<OnlineSession> };

  get globalChatUrl(): string { return this.api.globalChatUrl; }

  get apiClient(): AppApiClient { return this.api; }

  getChatSettings(): Promise<ChatSettings> { return this.api.getChatSettings(); }

  getAdminChatSettings(): Promise<ChatSettings> { return this.api.getAdminChatSettings(); }

  updateChatSettings(patch: Partial<ChatSettings>): Promise<ChatSettings> { return this.api.updateChatSettings(patch); }

  async getMatchmaking() { const session = await this.getOnlineSession(); return this.api.getMatchmaking(session.token); }
  async fetchRankedRecovery() {
    const session = await this.getOnlineSession();
    return this.api.fetchRankedRecovery(session.token);
  }
  async acceptRankedRecovery(matchId: string) {
    const session = await this.getOnlineSession();
    return this.api.acceptRankedRecovery(session.token, matchId);
  }
  async declineRankedRecovery(matchId: string): Promise<void> {
    const session = await this.getOnlineSession();
    return this.api.declineRankedRecovery(session.token, matchId);
  }
  async enqueueMatchmaking(search: MatchmakingSearchRequest) {
    const session = await this.getOnlineSession();
    return this.api.enqueueMatchmaking(session.token, search);
  }
  async cancelMatchmaking(): Promise<void> {
    const session = await this.getOnlineSession();
    return this.api.cancelMatchmaking(session.token);
  }
  async getOnlineRating(mode: MatchPlaylist['mode'], format: MatchPlaylist['format']) {
    const session = await this.getOnlineSession();
    return this.api.getOnlineRating(session.token, mode, format);
  }
  getOnlineSettings(): Promise<PublicOnlineSettings> { return this.api.getOnlineSettings(); }
  getAdminOnlineSettings(): Promise<OnlineSettings> { return this.api.getAdminOnlineSettings(); }
  updateAdminOnlineSettings(revision: number, patch: Partial<Omit<OnlineSettings, 'revision'>>): Promise<OnlineSettings> {
    return this.api.updateAdminOnlineSettings(revision, patch);
  }

  async getOnlineSession(): Promise<OnlineSession> {
    const identity = this.current;
    const version = this.identityVersion;
    if (this.onlineSession?.identity === identity && this.onlineSession.session.expiresAt > Date.now() + 30_000) {
      return this.onlineSession.session;
    }
    if (this.onlineRequest?.identity === identity && this.onlineRequest.version === version) return this.onlineRequest.promise;
    let guestToken: string | undefined;
    if (identity.kind === 'guest') {
      guestToken = this.onlineSession?.identity === identity ? this.onlineSession.session.token : undefined;
      if (!guestToken) {
        try { guestToken = window.localStorage.getItem(ONLINE_GUEST_STORAGE_KEY) ?? undefined; } catch { /* Storage is optional. */ }
      }
    }
    const promise = this.api.createOnlineSession(identity.kind === 'authenticated', guestToken).catch((error: unknown) => {
      if (guestToken && error instanceof AppApiError && error.status === 401
        && this.current === identity && this.identityVersion === version) {
        return this.api.createOnlineSession(false);
      }
      throw error;
    }).then((session) => {
      if (this.current !== identity || this.identityVersion !== version) throw new Error('Identity changed while opening online session.');
      this.onlineSession = { identity, session };
      if (session.identity.kind === 'guest') {
        try { window.localStorage.setItem(ONLINE_GUEST_STORAGE_KEY, session.token); } catch { /* Keep in memory when storage is unavailable. */ }
      }
      return session;
    });
    this.onlineRequest = { identity, version, promise };
    try { return await promise; }
    finally { if (this.onlineRequest?.promise === promise) this.onlineRequest = undefined; }
  }

  get snapshot(): BrowserIdentity {
    return this.current;
  }

  get displayName(): string {
    return this.current.kind === 'authenticated' && this.current.profile.displayName
      ? this.current.profile.displayName
      : this.guest.displayName;
  }

  get loginConfigured(): boolean {
    return this.auth !== null;
  }

  async initialize(): Promise<void> {
    await this.restoreIdentity(++this.identityVersion);
  }

  private async restoreIdentity(version: number): Promise<void> {
    if (!this.auth) return;
    const { data, error } = await this.auth.getSession();
    if (version !== this.identityVersion) return;
    if (error) throw new Error(error.message ?? 'Could not restore authenticated session.');
    if (!data?.user) {
      this.current = { kind: 'guest', guest: this.guest };
      return;
    }
    const profile = await this.api.getProfile();
    if (version !== this.identityVersion) return;
    this.current = {
      kind: 'authenticated',
      email: typeof data.user.email === 'string' ? data.user.email : null,
      profile,
    };
  }

  async signInWithGoogle(): Promise<void> {
    if (!this.auth) throw new Error('Google login is not configured for this environment.');
    const version = ++this.identityVersion;
    const result = await this.auth.signIn.social({
      provider: 'google',
      callbackURL: window.location.origin,
    });
    if (result.error) throw new Error(result.error.message ?? 'Google login failed.');
    if (version === this.identityVersion) await this.restoreIdentity(version);
  }

  async signOut(): Promise<void> {
    const version = ++this.identityVersion;
    if (this.auth) {
      const result = await this.auth.signOut();
      if (result.error) throw new Error(result.error.message ?? 'Could not sign out.');
    }
    if (version !== this.identityVersion) return;
    this.current = { kind: 'guest', guest: this.guest };
    this.onlineSession = undefined;
  }

  async completeOnboarding(input: {
    readonly firstName: string;
    readonly lastName: string;
    readonly displayName: string;
  }): Promise<void> {
    if (this.current.kind !== 'authenticated') throw new Error('Authentication is required.');
    const identity = this.current;
    const version = this.identityVersion;
    const profileVersion = ++this.profileVersion;
    const profile = await this.api.completeOnboarding(input);
    if (version === this.identityVersion && profileVersion === this.profileVersion && this.current === identity) {
      this.current = { ...identity, profile };
    }
  }

  async changeDisplayName(displayName: string): Promise<void> {
    if (this.current.kind !== 'authenticated') throw new Error('Authentication is required.');
    const identity = this.current;
    const version = this.identityVersion;
    const profileVersion = ++this.profileVersion;
    const profile = await this.api.changeDisplayName(displayName);
    if (version === this.identityVersion && profileVersion === this.profileVersion && this.current === identity) {
      this.current = { ...identity, profile };
    }
  }

  private async accessToken(): Promise<string> {
    if (!this.tokenProvider) throw new Error('Authentication is not configured.');
    return this.tokenProvider.getToken();
  }
}
